<template>
  <dd v-if="transcription.deleted" class="entry-body">
    <span class="entry-deleted">
      <span class="entry-deleted-icon">
        <Icon name="general.trash" size="xs" />
      </span>
      Block deleted
      <a href="#" class="text-link" data-testid="transcription-list-item-restore" @click.prevent="restoreLine">Restore</a>
    </span>
  </dd>
  <ZtPopup
    v-else
    ref="ztPopupRef"
    placement="top"
    style="margin: 0; padding: 0; border: 0"
    :escapeKey="false"
    @open:popup="onOpen"
    @keydown.escape="restoreLine"
    @keydown.enter.ctrl.exact="onSave"
    @keydown.enter.meta.exact="onSave"
  >
    <dd class="entry-body entry-hoverable" data-testid="transcription-list-item--edit">
      <span v-if="isLineDirty(transcription)" class="entry-edited-icon">
        <Icon name="general.edited" size="xs" />
      </span>
      <span>{{ props.transcription.newWords || props.transcription.words }}</span>
    </dd>
    <template #content>
      <div class="entry-edit">
        <FormKit :id="inputId" ref="wordsRef" rows="2" type="textarea" data-testid="transcription-list-item-input" v-model="words" />
        <div class="flex justify-between">
          <div class="button-group">
            <Button
              type="link"
              variant="black"
              icon="general.trash"
              class="!text-red-500 dark:!text-red-400 active:!text-white active:dark:!text-white"
              data-testid="transcription-list-item-delete"
              @click.prevent="handlePopupButtonClick('delete')"
              >Delete block</Button
            >
            <Button
              type="link"
              variant="black"
              icon="general.refresh"
              class="!text-blue-500 active:!text-white"
              data-testid="transcription-list-item-reset"
              :disabled="isRestoreDisabled"
              @click.prevent="handlePopupButtonClick('reset')"
              >Reset</Button
            >
          </div>
          <Button type="solid" variant="blue" class="!w-14" data-testid="transcription-list-item-save" @click.prevent="() => handlePopupButtonClick('update')"
            >OK</Button
          >
        </div>
      </div>
    </template>
  </ZtPopup>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, Ref } from 'vue';
import { Button, Icon, ZtPopup } from '@/storybook';
import { ItemTranscriptionEdit, useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { FormKitNode, reset, getNode } from '@formkit/core';
import { useFormKit } from '@/composables/useFormKit';
import { TrackEvents, useTrack } from '@/composables/useTrack';
import { storeToRefs } from 'pinia';
import { useConfirmModal } from '../../../../../../storybook/modal/useModal';

const { getContext } = useFormKit();
const { trackItemOwner } = useTrack();

const props = defineProps<{
  transcription: ItemTranscriptionEdit;
}>();

const transcriptionStore = useTranscriptionStore();
const { isLineDirty } = storeToRefs(transcriptionStore);

const inputId = `transcription-list-input-${props.transcription.start}`.replace(/\./g, '');
const isRestoreDisabled = computed(() => !wordsRefDirty.value && !isLineDirty.value(props.transcription));
const words = ref(props.transcription.words);
// https://formkit.com/essentials/architecture#using-a-template-ref
const wordsRef: Ref<{ node: FormKitNode } | null> = ref(null);
const wordsRefDirty = computed(() => getContext(wordsRef)?.state?.dirty);
const ztPopupRef: Ref<InstanceType<typeof ZtPopup> | null> = ref(null);

const restoreLine = async (e?: KeyboardEvent | MouseEvent) => {
  const node = getNode(inputId);

  if (node && node.context?.state.dirty) {
    const { open } = useConfirmModal({
      title: 'Cancel & discard changes?',
      confirmText: 'Discard changes',
      cancelText: 'Back to editing',
    });

    const confirmed = await open();

    if (!confirmed) {
      e?.preventDefault();
      e?.stopPropagation();
      return;
    }
  }

  const line = props.transcription;

  if (line.deleted) {
    trackItemOwner(TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_RESTORE);
  } else {
    trackItemOwner(TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_RESET);
  }

  // although we reset the words, we need to update the words value
  // to trigger the FormKit to update the value in the input
  words.value = line.words;
  wordsRef.value && reset(wordsRef?.value?.node, line.words);
  transcriptionStore.lineRestore(line);

  ztPopupRef.value?.closePopup();
};
const updateLine = (words: string) => {
  trackItemOwner(TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_ACCEPT);
  transcriptionStore.lineUpdate(props.transcription, { words });
};

const handlePopupButtonClick = (action: 'delete' | 'reset' | 'update') => {
  switch (action) {
    case 'delete':
      trackItemOwner(TrackEvents.VIEWER_TRANSCRIPTION_BLOCK_DELETE);
      transcriptionStore.lineDelete(props.transcription);
      break;
    case 'reset':
      restoreLine();
      break;
    case 'update':
      updateLine(words.value);
      break;
  }
  ztPopupRef.value?.closePopup();
};

const onOpen = () => {
  nextTick(() => {
    const textarea = document.getElementById(inputId) as HTMLTextAreaElement | null;
    if (textarea) {
      textarea.focus();
      textarea.selectionStart = words?.value.length;
    }
  });
};
const onSave = () => {
  updateLine(words.value);

  // Scripts may close only the windows that were opened by them
  ztPopupRef.value?.closePopup();
};
</script>
<style scoped>
.entry-body {
  @apply flex-grow dark:text-zinc-300;
}

.entry-deleted {
  @apply flex gap-1 items-center text-slate-500;
}

.entry-edited-icon {
  @apply inline-block align-text-top text-slate-500 mr-1;
}

.entry-hoverable {
  @apply hover:bg-blue-100 dark:hover:bg-zinc-700 cursor-pointer -m-1 p-1 rounded-md;
}

.entry-edit {
  @apply flex flex-col gap-2.5 bg-white dark:bg-zinc-700 shadow-md w-[400px] p-2.5 rounded-10;
}

.button-group {
  @apply flex gap-2 items-center;
}
</style>
